import {
  AlertTriangleIcon,
  BinaryIcon,
  CodeIcon,
  CogIcon,
  FileTextIcon,
  LayoutDashboardIcon,
  NetworkIcon,
  ServerIcon,
  UsersIcon,
  WaypointsIcon,
} from '@lucide/vue'
import type { Component } from 'vue'
import type { RouteRecordNameGeneric } from 'vue-router'

export type SidebarItem = {
  title: string
  routeName: string
  icon: Component
  children?: { title: string; routeName: string }[]
}
export type SidebarGroup = {
  label: string
  items: SidebarItem[]
}

/**
 * Sidebar entries, by route name. Routes without an entry (e.g. Anvil) are
 * reachable by URL only.
 */
export const sidebarGroups: SidebarGroup[] = [
  {
    label: 'Monitor',
    items: [
      { title: 'Dashboard', routeName: 'dashboard', icon: LayoutDashboardIcon },
      {
        title: 'Findings',
        routeName: 'findings',
        icon: AlertTriangleIcon,
      },
      {
        title: 'Assets',
        routeName: 'assets',
        icon: NetworkIcon,
        children: [
          { title: 'Network Map', routeName: 'asset:all:map' },
          { title: 'List', routeName: 'asset:all:list' },
          { title: 'Network Activity', routeName: 'asset:network-activity' },
        ],
      },
      {
        title: 'Network Events',
        routeName: 'network-events',
        icon: WaypointsIcon,
      },
    ],
  },
  {
    label: 'Platform',
    items: [
      {
        title: 'Deployment',
        routeName: 'platform:deployment',
        icon: ServerIcon,
      },
      { title: 'Users', routeName: 'platform:users', icon: UsersIcon },
      {
        title: 'Settings',
        routeName: 'platform:settings',
        icon: CogIcon,
        children: [
          {
            title: 'Notifications',
            routeName: 'platform:settings:notifications',
          },
          {
            title: 'Customizations',
            routeName: 'platform:settings:customizations',
          },
        ],
      },
    ],
  },
  {
    label: 'Tools',
    items: [
      { title: 'API', routeName: 'api', icon: CodeIcon },
      {
        title: 'PCAP Upload',
        routeName: 'tools:pcap_uploads',
        icon: BinaryIcon,
      },
      {
        title: 'Reports',
        routeName: 'reports',
        icon: FileTextIcon,
        children: [
          { title: 'Findings summary', routeName: 'reports:findings' },
          { title: 'Hunt reports', routeName: 'reports:hunt' },
        ],
      },
    ],
  },
]

/** The sidebar item that `routeName` is, or is a child of. */
export const sidebarItemFor = (
  routeName: RouteRecordNameGeneric,
): SidebarItem | undefined =>
  sidebarGroups
    .flatMap((group) => group.items)
    .find(
      (item) =>
        item.routeName === routeName ||
        item.children?.some((child) => child.routeName === routeName),
    )
